import React, { FC } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { IInvite, INVITE_FIELDS } from '../types/invite';
import { GridTooltip } from './General/GridTooltip';
import { ErrorOverlayDataGrid } from './ErrorOverlayDataGrid';
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useNavigate } from 'react-router-dom';
import { getConsistentColor } from '../helpers/colorUtils';

interface InvitesDataGridProps {
  invites: IInvite[];
  loading?: boolean;
  error?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onDeleteInvite: (inviteId: string, inviteEmail: string) => void;
  onResendInvite: (inviteId: string, inviteEmail: string) => void;
  showDeleteButton?: boolean;
  showResendButton?: boolean;
}

const formatDateToDDMMYYYY = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const STATUS_CONFIG: Record<
  string,
  { color: 'warning' | 'success' | 'default'; icon: React.ReactElement }
> = {
  pending: { color: 'warning', icon: <ScheduleOutlinedIcon fontSize="small" /> },
  accepted: { color: 'success', icon: <CheckCircleOutlinedIcon fontSize="small" /> },
  expired: { color: 'default', icon: <BlockOutlinedIcon fontSize="small" /> },
};

const cellSx = { height: '100%', overflow: 'hidden', alignItems: 'flex-start' };

const InvitesDataGrid: FC<InvitesDataGridProps> = ({
  invites,
  loading = false,
  error = false,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDeleteInvite,
  onResendInvite,
  showDeleteButton = true,
  showResendButton = true,
}) => {
  const navigate = useNavigate();
  const columns: GridColDef[] = [
    {
      field: INVITE_FIELDS.EMAIL,
      headerName: 'Email',
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Stack justifyContent="center" sx={cellSx}>
          <GridTooltip params={params} />
        </Stack>
      ),
    },
    {
      field: INVITE_FIELDS.CREATED_AT,
      headerName: 'Created date',
      minWidth: 160,
      flex: 1,
      type: 'dateTime',
      valueGetter: (_value, row) => (row.createdAt ? new Date(row.createdAt) : null),
      renderCell: (params: GridRenderCellParams) => (
        <Stack justifyContent="center" sx={cellSx}>
          <GridTooltip params={{ ...params, value: formatDateToDDMMYYYY(params.row.createdAt) }} />
        </Stack>
      ),
    },
    {
      field: INVITE_FIELDS.EXPIRATION_DATE,
      headerName: 'Expiry date',
      minWidth: 160,
      flex: 1,
      type: 'dateTime',
      valueGetter: (_value, row) => (row.expirationDate ? new Date(row.expirationDate) : null),
      renderCell: (params: GridRenderCellParams) => {
        const isExpired = params.row.status?.toLowerCase() === 'expired';
        const formatted = formatDateToDDMMYYYY(params.row.expirationDate);
        return (
          <Stack justifyContent="center" sx={cellSx}>
            <Tooltip title={formatted}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  color: isExpired ? 'error.main' : 'inherit',
                  textDecoration: isExpired ? 'line-through' : 'none',
                }}>
                {formatted}
              </Typography>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: INVITE_FIELDS.STATUS,
      headerName: 'Status',
      minWidth: 130,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const key = params.value?.toLowerCase();
        const config = STATUS_CONFIG[key];
        if (!config)
          return (
            <Stack justifyContent="center" sx={cellSx}>
              <GridTooltip params={params} />
            </Stack>
          );
        return (
          <Stack justifyContent="center" sx={cellSx}>
            <Chip icon={config.icon} label={params.value} color={config.color} size="small" />
          </Stack>
        );
      },
    },
    {
      field: INVITE_FIELDS.ROLE_NAME,
      headerName: 'Role',
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const isMainAccount = params.value?.toLowerCase() === 'mainaccount';
        return (
          <Stack justifyContent="center" sx={cellSx}>
            {isMainAccount ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <StarOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2">Main account</Typography>
              </Stack>
            ) : (
              <Typography variant="body2">{params.value}</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: INVITE_FIELDS.ACCEPTED_BY,
      headerName: 'Accepted by',
      minWidth: 280,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return (
            <Stack justifyContent="center" sx={cellSx}>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                -
              </Typography>
            </Stack>
          );
        }

        const color = getConsistentColor(params.value);

        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
            <Tooltip title={`View user details for ${params.value}`}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/user/detail/${params.value}`);
                }}
                sx={{
                  cursor: 'pointer',
                  overflow: 'hidden',
                  flex: 1,
                  minWidth: 0,
                  '&:hover .link-icon': { opacity: 1 },
                }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                  {params.value}
                </Typography>
                <OpenInNewIcon
                  className="link-icon"
                  sx={{ fontSize: 14, opacity: 0.4, flexShrink: 0, color: 'text.secondary' }}
                />
              </Stack>
            </Tooltip>
            <Tooltip title="Copy">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(params.value);
                }}
                sx={{ flexShrink: 0, ml: 0.5 }}>
                <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    ...(showDeleteButton || showResendButton
      ? [
          {
            field: 'actions',
            headerName: '',
            width: showDeleteButton && showResendButton ? 100 : 60,
            sortable: false,
            filterable: false,
            headerAlign: 'right' as const,
            align: 'right' as const,
            renderCell: (params: GridRenderCellParams) => {
              const status = params.row.status?.toLowerCase();
              const isAccepted = status === 'accepted';
              const isPendingOrExpired = status === 'pending' || status === 'expired';

              return (
                <Stack
                  direction="row"
                  spacing={0.5}
                  justifyContent="flex-end"
                  alignItems="center"
                  sx={{ height: '100%', overflow: 'hidden' }}>
                  {showResendButton && isPendingOrExpired && (
                    <Tooltip title="Resend invite">
                      <IconButton
                        size="small"
                        onClick={() => onResendInvite(params.row.id, params.row.email)}
                        data-cy="resend-invite-button">
                        <RefreshOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {showDeleteButton && !isAccepted && (
                    <Tooltip title="Delete invite">
                      <IconButton
                        size="small"
                        onClick={() => onDeleteInvite(params.row.id, params.row.email)}
                        data-cy="delete-invite-button">
                        <DeleteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              );
            },
          },
        ]
      : []),
  ];

  if (error) {
    return <ErrorOverlayDataGrid />;
  }

  return (
    <DataGrid
      autoHeight
      columns={columns}
      getRowId={(row) => row.id}
      loading={loading}
      pagination
      paginationMode="server"
      rowCount={totalCount}
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
        if (newPageSize !== pageSize) {
          onPageSizeChange(newPageSize);
        } else {
          onPageChange(newPage);
        }
      }}
      pageSizeOptions={[5, 10, 20]}
      rows={invites || []}
      disableRowSelectionOnClick
      checkboxSelection={false}
      rowHeight={52}
      initialState={{
        sorting: {
          sortModel: [{ field: INVITE_FIELDS.CREATED_AT, sort: 'desc' }],
        },
      }}
      sx={{ backgroundColor: 'white' }}
    />
  );
};

export default InvitesDataGrid;
